import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  FormControl,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import axios from "api";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useSelector } from "react-redux";
import { RootState } from "store";
import dayjs from "dayjs";
import { formatMoney } from "utils/money";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface TransactionRow {
  id: number;
  asset_name: string | null;
  asset_type_name: string | null;
  order_id: string | null;
  payment_id: string | null;
  created_at: string | null;
  plan_name: string | null;
  amount: number | string | null;
  total_amount?: number | string | null;
  currency: string | null;
  status?: string | null;
}

interface AssetTypeOption {
  asset_type_id: number;
  asset_type_name: string;
}

interface AssetOption {
  asset_id: number;
  asset_name: string;
  asset_type_id: number;
  asset_type_name: string;
}

type DatePreset = "week" | "month" | "year" | "custom" | "all";
const checkIsCustomer = (user: RootState["auth"]["user"]): boolean => {
  if (!user) return false;
  // Cast through unknown first to safely widen the type before narrowing
  const u = user as unknown as Record<string, unknown>;
  if (Array.isArray(u["Role"])) {
    const hasCustomerRole = (u["Role"] as unknown[]).some(
      (role) => typeof role === "string" && role.toLowerCase() === "customer"
    );
    if (hasCustomerRole) return true;
  }
  if (typeof u["type"] === "string") {
    return u["type"].toLowerCase() === "customer";
  }
  if (typeof u["customer_id"] !== "undefined") {
    return Boolean(u["customer_id"]);
  }
  return false;
};

const resolveAmount = (row: TransactionRow & { converted_amount?: number | string | null }): number => {
  if (row?.converted_amount != null) return Number(row.converted_amount);
  if (row?.total_amount != null) return Number(row.total_amount);
  return Number(row?.amount ?? 0);
};

interface AmountCellProps {
  row: TransactionRow & { converted_amount?: number | string | null };
  clientCurrency: string;
}

const AmountCell: React.FC<AmountCellProps> = ({ row, clientCurrency }) => {
  const amountToShow = resolveAmount(row);
  const rowCurrency = row?.currency || clientCurrency || "USD";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 0.6,
      }}
    >
      <Typography component="span">
        {formatMoney(amountToShow, String(rowCurrency))}
      </Typography>
    </Box>
  );
};

interface InvoiceCellProps {
  row: TransactionRow;
  onDownload: (id?: number | null) => void;
}

const InvoiceCell: React.FC<InvoiceCellProps> = ({ row, onDownload }) => {
  const id = row?.id;
  const isPaid = String(row?.status || "").toUpperCase() === "PAID";
  return (
    <IconButton
      aria-label="download invoice"
      size="small"
      disabled={!id || !isPaid}
      onClick={() => onDownload(id)}
    >
      <DownloadOutlinedIcon fontSize="small" />
    </IconButton>
  );
};

interface PresetButtonProps {
  label: string;
  preset: DatePreset;
  active: DatePreset;
  onSelect: (preset: DatePreset) => void;
}

const PresetButton: React.FC<PresetButtonProps> = ({ label, preset, active, onSelect }) => (
  <Button
    onClick={() => onSelect(preset)}
    variant="outlined"
    color={active === preset ? "primary" : "inherit"}
    sx={{
      fontWeight: active === preset ? 600 : 400,
      color: active === preset ? "primary.main" : "text.primary",
      ...(active === preset ? { borderWidth: 2 } : {}),
    }}
  >
    {label}
  </Button>
);

const CustomerTransactions: React.FC = () => {
  const { fetchError } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const clientCurrency =
    (user as { client_currency?: string })?.client_currency ||
    (user as { currency_code?: string })?.currency_code ||
    "USD";
  const [rows, setRows] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [assetTypes, setAssetTypes] = useState<AssetTypeOption[]>([]);
  const [assetOptions, setAssetOptions] = useState<AssetOption[]>([]);
  const [assetTypeFilter, setAssetTypeFilter] =
    useState<AssetTypeOption | null>(null);
  const [assetFilter, setAssetFilter] = useState<AssetOption | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<"all" | "success" | "failed">(
    "all"
  );
  const [datePreset, setDatePreset] = useState<DatePreset>("month");
  const [rangeStart, setRangeStart] = useState(
    () => dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [rangeEnd, setRangeEnd] = useState(() =>
    dayjs().endOf("month").format("YYYY-MM-DD")
  );

  const dateQuery = useMemo(() => {
    if (datePreset === "all") return {};
    if (datePreset === "custom") {
      return { startDate: rangeStart, endDate: rangeEnd };
    }
    if (datePreset === "week") {
      return {
        startDate: dayjs().startOf("week").format("YYYY-MM-DD"),
        endDate: dayjs().endOf("week").format("YYYY-MM-DD"),
      };
    }
    if (datePreset === "year") {
      return {
        startDate: dayjs().startOf("year").format("YYYY-MM-DD"),
        endDate: dayjs().endOf("year").format("YYYY-MM-DD"),
      };
    }
    return {
      startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
      endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
    };
  }, [datePreset, rangeStart, rangeEnd]);

  const isCustomer = useMemo(() => checkIsCustomer(user), [user]);

  const fetchAssets = useCallback(async () => {
    if (!isCustomer || !user?.userId) {
      setAssetOptions([]);
      setAssetTypes([]);
      return;
    }
    try {
      const res = await axios.get(`/assets/customer/${user.userId}`);
      const rawList: Record<string, unknown>[] = res.data?.list ?? [];
      const list: AssetOption[] = rawList.map((asset) => ({
        asset_id: asset.asset_id as number,
        asset_name: (asset.asset_name ?? asset.asset_service_id ?? "Asset") as string,
        asset_type_id: asset.asset_type_id as number,
        asset_type_name: (asset.asset_type_name ?? "Asset Type") as string,
      }));
      setAssetOptions(list);
      const uniqueMap = new Map<number, AssetTypeOption>();
      list.forEach((asset) => {
        if (!uniqueMap.has(asset.asset_type_id)) {
          uniqueMap.set(asset.asset_type_id, {
            asset_type_id: asset.asset_type_id,
            asset_type_name: asset.asset_type_name,
          });
        }
      });
      setAssetTypes(Array.from(uniqueMap.values()));
    } catch {
      setAssetOptions([]);
      setAssetTypes([]);
    }
  }, [isCustomer, user?.userId]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const filteredAssetOptions = useMemo(() => {
    if (!assetTypeFilter) return assetOptions;
    return assetOptions.filter(
      (asset) => asset.asset_type_id === assetTypeFilter.asset_type_id
    );
  }, [assetOptions, assetTypeFilter]);

  useEffect(() => {
    if (
      assetFilter &&
      assetTypeFilter &&
      assetFilter.asset_type_id !== assetTypeFilter.asset_type_id
    ) {
      setAssetFilter(null);
    }
  }, [assetFilter, assetTypeFilter]);

  const fetchTransactions = useCallback(async () => {
    if (!isCustomer) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, string | number> = {};
      if ("startDate" in dateQuery && dateQuery.startDate && "endDate" in dateQuery && dateQuery.endDate) {
        params.startDate = dateQuery.startDate;
        params.endDate = dateQuery.endDate;
      }
      if (assetTypeFilter?.asset_type_id) {
        params.assetTypeId = assetTypeFilter.asset_type_id;
      }
      if (assetFilter?.asset_id) {
        params.assetId = assetFilter.asset_id;
      }
      if (paymentStatus !== "all") {
        params.paymentStatus = paymentStatus === "failed" ? "failed" : "success";
      }
      const res = await axios.get("/customer-subscriptions/transactions/self", {
        params,
      });
      setRows(res.data?.transactions ?? []);
    } catch (err: unknown) {
      fetchError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          "Unable to load transactions right now."
      );
    } finally {
      setLoading(false);
    }
  }, [assetFilter, assetTypeFilter, dateQuery, fetchError, isCustomer, paymentStatus]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    if (!isCustomer) {
      navigate("/dashboard", { replace: true });
    }
  }, [isCustomer, navigate]);

  const clearFilters = () => {
    setAssetTypeFilter(null);
    setAssetFilter(null);
    setPaymentStatus("all");
    setDatePreset("month");
    setRangeStart(dayjs().startOf("month").format("YYYY-MM-DD"));
    setRangeEnd(dayjs().endOf("month").format("YYYY-MM-DD"));
  };

  const filterTextFieldSx = {
    "& .MuiInputBase-input": { color: "text.primary" },
    "& .MuiInputBase-input::placeholder": {
      color: "text.secondary",
      opacity: 1,
    },
    "& .MuiSvgIcon-root": { color: "text.secondary" },
  } as const;

  const handleDownloadInvoice = useCallback(
    async (transactionId?: number | null) => {
      if (!transactionId) {
        fetchError("Invoice not available for this transaction.");
        return;
      }
      try {
        const res = await axios.get(
          `/customer-subscriptions/transactions/${transactionId}/invoice`,
          { responseType: "blob" }
        );
        const rawContentType = res.headers["content-type"];
        const contentType =
          typeof rawContentType === "string"
            ? rawContentType
            : Array.isArray(rawContentType) && typeof rawContentType[0] === "string"
              ? rawContentType[0]
              : "application/pdf";
        const disposition = res.headers["content-disposition"];
        const blob = new Blob([res.data], { type: contentType });
        let downloadName = `customer-invoice-${transactionId}.pdf`;
        if (disposition) {
          // FIX 5: Removed unnecessary escape characters \" — use plain " inside character class
          const match = disposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
          if (match?.[1]) {
            // FIX 5: Removed unnecessary escape characters \" in replace regex
            const raw = match[1].replace(/"/g, "");
            try {
              downloadName = decodeURIComponent(raw);
            } catch {
              downloadName = raw;
            }
          }
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (err: unknown) {
        fetchError(
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            "Could not download the invoice file."
        );
      }
    },
    [fetchError]
  );

  const columns: MUIDataTableColumn[] = useMemo(
    () => [
      {
        name: "asset_type_name",
        label: "Asset Type",
        options: {
          customBodyRender: (value) => value || "-",
        },
      },
      {
        name: "asset_name",
        label: "Asset",
        options: {
          customBodyRender: (value) => value || "-",
        },
      },
      {
        name: "order_id",
        label: "Order ID",
        options: {
          customBodyRender: (value) => value || "-",
        },
      },
      {
        name: "payment_id",
        label: "Payment ID",
        options: {
          customBodyRender: (value) => value || "-",
        },
      },
      {
        name: "created_at",
        label: "Payment Date",
        options: {
          customBodyRender: (value) =>
            value ? dayjs(value).format("MMM D, YYYY HH:mm") : "-",
          setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
        },
      },
      {
        name: "plan_name",
        label: "Subscription",
        options: {
          customBodyRender: (value) => value || "-",
        },
      },
      {
        name: "amount",
        label: "Amount",
        options: {
          // FIX 4: AmountCell moved outside parent component; data passed as props
          customBodyRenderLite: (dataIndex) => {
            const row = rows[dataIndex];
            return <AmountCell row={row} clientCurrency={clientCurrency} />;
          },
          setCellProps: () => ({ style: { textAlign: "right" } }),
        },
      },
      {
        name: "id",
        label: "Invoice",
        options: {
          sort: false,
          filter: false,
          customBodyRenderLite: (dataIndex) => {
            const row = rows[dataIndex];
            return <InvoiceCell row={row} onDownload={handleDownloadInvoice} />;
          },
        },
      },
    ],
    [handleDownloadInvoice, rows, clientCurrency]
  );

  const tableOptions: MUIDataTableOptions = useMemo(
    () => ({
      selectableRows: "none",
      selectableRowsHeader: false,
      elevation: 0,
      tableBodyHeight: "auto",
      responsive: "standard",
      search: false,
      download: false,
      print: false,
      textLabels: {
        body: {
          noMatch: loading
            ? "Loading customer transactions..."
            : "No transactions found.",
        },
      },
    }),
    [loading]
  );

  if (!isCustomer) {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card sx={{ p: 2, mb: 4, maxWidth: 1100, mx: "auto" }}>
        <CardContent>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            Transactions
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "flex-end",
              mb: 2,
            }}
          >
            <FormControl sx={{ width: 240 }}>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>
                Asset Type
              </Typography>
              <Autocomplete
                options={assetTypes}
                getOptionLabel={(option) => option.asset_type_name ?? ""}
                value={assetTypeFilter}
                onChange={(_event, value) => setAssetTypeFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All asset types"
                    size="small"
                    sx={filterTextFieldSx}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={{ width: 240 }}>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>
                Asset
              </Typography>
              <Autocomplete
                options={filteredAssetOptions}
                getOptionLabel={(option) => option.asset_name ?? ""}
                value={assetFilter}
                onChange={(_event, value) => setAssetFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All assets"
                    size="small"
                    sx={filterTextFieldSx}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={{ width: 200 }}>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>
                Status
              </Typography>
              <TextField
                select
                size="small"
                value={paymentStatus}
                onChange={(e) =>
                  setPaymentStatus(e.target.value as "all" | "success" | "failed")
                }
                sx={filterTextFieldSx}
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failed">Failed / Missed</MenuItem>
              </TextField>
            </FormControl>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              {/* FIX 1: PresetButton extracted to eliminate repeated inline ternary branches */}
              <ButtonGroup size="small" variant="outlined">
                <PresetButton label="This week" preset="week" active={datePreset} onSelect={setDatePreset} />
                <PresetButton label="This month" preset="month" active={datePreset} onSelect={setDatePreset} />
                <PresetButton label="This year" preset="year" active={datePreset} onSelect={setDatePreset} />
                <PresetButton label="All" preset="all" active={datePreset} onSelect={setDatePreset} />
                <PresetButton label="Custom" preset="custom" active={datePreset} onSelect={setDatePreset} />
              </ButtonGroup>
              {datePreset === "custom" && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "flex-end",
                  }}
                >
                  <FormControl sx={{ minWidth: 160 }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ pb: 0.75, display: "block" }}
                    >
                      From
                    </Typography>
                    <TextField
                      size="small"
                      type="date"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                      variant="outlined"
                    />
                  </FormControl>
                  <FormControl sx={{ minWidth: 160 }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ pb: 0.75, display: "block" }}
                    >
                      To
                    </Typography>
                    <TextField
                      size="small"
                      type="date"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      variant="outlined"
                    />
                  </FormControl>
                </Box>
              )}
            </Box>
            {(assetTypeFilter || assetFilter || datePreset !== "month") && (
              <Button variant="outlined" color="primary" size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </Box>
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={undefined}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default CustomerTransactions;
